import { type SearchRequest } from '@searchkit/api';
import { merge } from 'merge-anything';

import { type BettingInsightsElasticSearch } from '~/configuration/betting-insights/elasticsearch/BettingInsightsElasticSearch.ts';
import {
	type BettingInsightsResultAttribute,
	type TrendsIndex,
} from '~/configuration/betting-insights/types.ts';
import { featureFlags } from '~/providers/feature-flag-provider.tsx';
import { convertSortingConfigToSortingValue } from '~/utils/convertSortingConfigToSortingValue.ts';
import { splitFacetFilter } from '~/utils/splitFacetFilter.ts';

import { hitRateRequiredQueryFactory } from '../queries/hitRateStatRequiredQueryFactory.ts';
import { mergeQueries } from '../utils/mergeQueries.ts';

export type TrendsFacetFilter =
	`${BettingInsightsResultAttribute<TrendsIndex>}:${string}`;

export function trendsQueryFactory(
	config: BettingInsightsElasticSearch<TrendsIndex>,
) {
	return (searchRequest: SearchRequest): SearchRequest => {
		const indexName = searchRequest.indexName;
		const { hitsPerPage } = searchRequest.request.params ?? { hitsPerPage: 10 };

		let facetFilters: TrendsFacetFilter[][] =
			(searchRequest.request.params?.facetFilters as TrendsFacetFilter[][]) ??
			[];
		if (!Array.isArray(facetFilters)) {
			facetFilters = [facetFilters];
		}

		const currentRefinements = facetFilters.map(([facetFilter]) => {
			const [attribute, label] = splitFacetFilter<
				TrendsIndex,
				TrendsFacetFilter
			>(facetFilter);
			return {
				attribute,
				refinements: [{ value: label }],
			};
		});

		const sorting = config.sorting({ currentRefinements });
		const defaultSort = sorting.options[0];

		if (!defaultSort) {
			throw new Error('Default sort not found');
		}

		const activeSort =
			sorting.options.find(
				sort =>
					`${indexName}${convertSortingConfigToSortingValue(sort)}` ===
					searchRequest.request.indexName,
			) ?? defaultSort;

		const activeSortFieldSegments = activeSort.field.split('.');

		const sort = {
			[activeSort.field]: activeSort.order,
		};

		const last10PersistentSortEnabled =
			config.featureFlags?.find(
				flag => flag.name === featureFlags.SUBS__PLUS__LAST_10_PERSISTENT_SORT,
			)?.enabled ?? false;

		const query = merge(searchRequest, {
			body: {
				aggs: {
					unique_bets: {
						cardinality: {
							field: 'betId',
						},
					},
				},
				collapse: {
					field: 'betId',
					inner_hits: {
						_source: {
							includes: config.results().attributes,
						},
						name: 'best_by_odds',
						size: 1,
						sort: [
							{
								oddsDecimal: 'desc',
							},
						],
					},
				},
				query: mergeQueries(
					searchRequest.body.query ?? {},
					...(activeSort.field.includes('sportsData.hitRates.last')
						? [
								hitRateRequiredQueryFactory({
									hitRateBucket:
										activeSortFieldSegments[activeSortFieldSegments.length - 1],
								}),
							]
						: []),
				),
				size: hitsPerPage,
				sort: [
					sort,
					...(last10PersistentSortEnabled &&
					!activeSort.field.includes('sportsData.hitRates.last')
						? [
								{
									'sportsData.hitRates.last10': 'desc' as const,
								},
							]
						: []),
				],
			},
		});

		return query;
	};
}
